import { useMemo } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { LibraryPicker } from "@/features/navigator/components/library-picker"
import { getAvailableIndexesForSession } from "@/features/navigator/utils/available-indexes"
import { useAIExplorerSettings } from "@/hooks/use-module-settings"
import { type Project, ProjectService } from "@/services/project-service"

type ProjectLibrariesSettingsProps = {
  project: Project | undefined
  projectId: string
  sessionType: string
  canEdit?: boolean
}

/**
 * "Pinned default libraries" — opt-in project-level override of the user's
 * last-used libraries for new chats. The pin is forwarded by
 * `useLibrariesSelection` into `useNavigatorLibrariesSync`, which seeds the
 * navigator store from the pin instead of user settings when one is active.
 */
export function ProjectLibrariesSettings({
  project,
  projectId,
  sessionType,
  canEdit = true,
}: ProjectLibrariesSettingsProps) {
  const queryClient = useQueryClient()
  const { t } = useTranslation("", { keyPrefix: "project_settings.libraries" })

  const updateProject = useMutation({
    mutationFn: (data: { defaultEnabledIndexIds?: string[] | null }) =>
      ProjectService.updateProject(projectId, data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["projects", sessionType] }),
    onError: (error: Error) => {
      toast.error(t("save_error"), {
        description: error.message || undefined,
      })
    },
  })

  // null/undefined → toggle off (use user's last-used libraries).
  // array (possibly empty) → toggle on, pinning that exact set.
  const pinnedIds = project?.defaultEnabledIndexIds ?? null
  const isPinned = Array.isArray(pinnedIds)
  const { settings: aiExplorerSettings } = useAIExplorerSettings(sessionType)

  // Libraries available for this session type. Uses the shared helper so the
  // filter rule stays in sync with the navigator store / libraries-selection hook.
  const availableLibraries = useMemo(
    () =>
      getAvailableIndexesForSession(sessionType).map((index) => ({
        id: index.id,
        name: index.display_name,
        description: index.description,
      })),
    [sessionType]
  )

  const handlePinnedToggle = (next: boolean) => {
    if (!canEdit) return
    if (next) {
      // Seed with the user's currently-checked libraries so the pin starts in
      // a sensible state instead of empty. Intersect with available so we don't
      // pin ids that don't exist for this session type.
      const userEnabled = aiExplorerSettings?.enabled_libraries ?? {}
      const availableIds = new Set(availableLibraries.map((lib) => lib.id))
      const seed = Object.entries(userEnabled)
        .filter(([id, lib]) => lib.checked && availableIds.has(id))
        .map(([id]) => id)
      updateProject.mutate({ defaultEnabledIndexIds: seed })
    } else {
      // Explicit null clears the pin (toggle off).
      updateProject.mutate({ defaultEnabledIndexIds: null })
    }
  }

  const handlePinnedChange = (ids: string[]) => {
    if (!canEdit) return
    updateProject.mutate({ defaultEnabledIndexIds: ids })
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label className="text-sm font-medium" htmlFor="pinned-libraries-switch">
              {t("pinned_libraries_title")}
            </Label>
            <p className="text-muted-foreground text-xs">{t("pinned_libraries_description")}</p>
          </div>
          <Switch
            checked={isPinned}
            disabled={!canEdit}
            id="pinned-libraries-switch"
            onCheckedChange={handlePinnedToggle}
          />
        </div>
        {isPinned ? (
          <LibraryPicker
            activeLibraryIds={pinnedIds}
            availableLibraries={availableLibraries}
            triggerLabel={t("pinned_libraries_picker_label")}
            onActivationChange={handlePinnedChange}
          />
        ) : (
          <p className="text-muted-foreground text-xs">{t("pinned_libraries_off_hint")}</p>
        )}
      </div>
    </div>
  )
}
